import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertCircle, ArrowLeft, Building2, Save } from "lucide-react";
import { getClient, updateClient } from "../../../api/chatbotApi";

const addressFields = [
  { key: "street", label: "Street address" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "postal_code", label: "Postal code" },
  { key: "country", label: "Country" },
];
const contactFields = [
  { key: "name", label: "Contact name" },
  { key: "email", label: "Email", type: "email" },
  { key: "phone", label: "Phone", type: "tel" },
  { key: "website", label: "Website", type: "url" },
];
const inputClass = "mt-1 w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:border-[#096dea] focus:outline-none";
const fieldValue = (value) => (value == null ? "" : String(value));

const clientForm = (client) => ({
  name: client.name || "",
  address: {
    street: fieldValue(client.address?.street),
    city: fieldValue(client.address?.city),
    state: fieldValue(client.address?.state),
    postal_code: fieldValue(client.address?.postal_code),
    country: fieldValue(client.address?.country) || "India",
  },
  contact_details: Object.fromEntries(
    contactFields.map(({ key }) => [key, fieldValue(client.contact_details?.[key])]),
  ),
});

const buildDetails = (original, fields, values) => {
  const details = { ...original };
  fields.forEach(({ key }) => delete details[key]);
  fields.forEach(({ key }) => {
    const value = values[key].trim();
    if (value) details[key] = value;
  });
  return details;
};

export default function ChatbotClientEditPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [form, setForm] = useState({ name: "", address: {}, contact_details: {} });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    getClient(clientId)
      .then((result) => {
        if (!active) return;
        setClient(result);
        setForm(clientForm(result));
      })
      .catch((err) => {
        if (active) setError(err.response?.data?.detail || err.message || "Failed to load client");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [clientId]);

  const handleSave = async () => {
    if (!form.name.trim()) return;

    setSaving(true);
    setError("");
    try {
      await updateClient(clientId, {
        name: form.name.trim(),
        address: buildDetails(client.address || {}, addressFields, form.address),
        contact_details: buildDetails(client.contact_details || {}, contactFields, form.contact_details),
      });
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Failed to update client");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div role="status" className="p-12 text-center text-sm text-slate-500">Loading client...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 text-sm text-slate-500">
        <Link to="/dashboard" className="inline-flex items-center gap-1 hover:text-slate-900">
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span>/</span>
        <span className="font-semibold text-slate-900">Edit Client</span>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6">
        <div className="mb-5 border-b border-slate-100 pb-4">
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
            <Building2 size={24} className="text-[#096dea]" /> Edit Client
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Update the details for {client?.name || "this client"}.
          </p>
        </div>

        {error && (
          <div role="alert" className="mb-5 flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        {client ? (
          <>
            <div className="space-y-6">
              <div className="max-w-2xl">
                <label htmlFor="edit-client-name" className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-700">
                  Client name *
                </label>
                <input
                  id="edit-client-name"
                  type="text"
                  value={form.name}
                  onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                  className={inputClass}
                  autoFocus
                />
              </div>

              <section aria-labelledby="edit-client-address-heading" className="rounded-xl border border-slate-200 p-4">
                <div className="mb-4">
                  <h2 id="edit-client-address-heading" className="text-sm font-bold text-slate-900">Address</h2>
                  <p className="mt-1 text-xs text-slate-500">All address fields are optional.</p>
                </div>
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  {addressFields.map(({ key, label }) => {
                    const id = `edit-client-address-${key}`;
                    return (
                      <div key={key} className={key === "street" ? "sm:col-span-2 xl:col-span-4" : undefined}>
                        <label htmlFor={id} className="block text-xs font-semibold uppercase tracking-wider text-slate-700">
                          {label}
                        </label>
                        <input
                          id={id}
                          type="text"
                          value={form.address[key] || ""}
                          onChange={(event) => setForm((current) => ({
                            ...current,
                            address: { ...current.address, [key]: event.target.value },
                          }))}
                          className={inputClass}
                        />
                      </div>
                    );
                  })}
                </div>
              </section>

              <section aria-labelledby="edit-client-contact-heading" className="rounded-xl border border-slate-200 p-4">
                <div className="mb-4">
                  <h2 id="edit-client-contact-heading" className="text-sm font-bold text-slate-900">Contact details</h2>
                  <p className="mt-1 text-xs text-slate-500">All contact fields are optional.</p>
                </div>
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  {contactFields.map(({ key, label, type = "text" }) => {
                    const id = `edit-client-contact-${key}`;
                    return (
                      <div key={key}>
                        <label htmlFor={id} className="block text-xs font-semibold uppercase tracking-wider text-slate-700">
                          {label}
                        </label>
                        <input
                          id={id}
                          type={type}
                          value={form.contact_details[key] || ""}
                          onChange={(event) => setForm((current) => ({
                            ...current,
                            contact_details: { ...current.contact_details, [key]: event.target.value },
                          }))}
                          className={inputClass}
                        />
                      </div>
                    );
                  })}
                </div>
              </section>
            </div>

            <div className="mt-6 flex justify-end gap-3 border-t border-slate-100 pt-5">
              <Link
                to="/dashboard"
                className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
              >
                Cancel
              </Link>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !form.name.trim()}
                className="inline-flex items-center gap-2 rounded-xl bg-[#096dea] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#0959bb] disabled:opacity-50"
              >
                <Save size={16} /> {saving ? "Saving..." : "Save Client"}
              </button>
            </div>
          </>
        ) : !error ? (
          <p role="alert" className="text-sm text-slate-600">Client not found.</p>
        ) : null}
      </section>
    </div>
  );
}
